import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["card", "column", "emailModal", "counter", "dropzone"];
  static values = {
    workspace: String,
    site: String,
  };

  connect() {
    console.log("Controller connected");
    console.log("Email modal target:", this.hasEmailModalTarget);
    this.draggedCard = null;
    this.originalColumn = null;
    this.pendingUpdate = null;
  }

  async drop(event) {
    event.preventDefault();
    const column = event.target.closest(
      "[data-appointment-kanban-target='column']"
    );
    if (!column || !this.draggedCard) return;

    const newStatus = column.dataset.status;
    const data = JSON.parse(event.dataTransfer.getData("application/json"));

    try {
      const response = await fetch(
        `/workspaces/${this.workspaceValue}/sites/${this.siteValue}/appointments/${data.appointmentId}/notification_check?status=${newStatus}`,
        {
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        }
      );

      const { should_notify } = await response.json();

      if (should_notify) {
        this.pendingUpdate = {
          appointmentId: data.appointmentId,
          status: newStatus,
        };
        this.emailModalTarget.classList.remove("hidden");
      } else {
        await this.updateAppointmentStatus(data.appointmentId, newStatus);
      }
    } catch (error) {
      console.error("Drop error:", error);
      this.showToast("Failed to process drop", "error");
    }
  }

  dragStart(event) {
    this.draggedCard = event.target;
    this.originalColumn = event.target.closest(
      "[data-appointment-kanban-target='column']"
    );
    event.target.classList.add("opacity-50");

    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        appointmentId: event.target.dataset.appointmentId,
        emailEnabled: event.target.dataset.emailEnabled,
      })
    );
  }

  dragEnd(event) {
    event.target.classList.remove("opacity-50");
    this.columnTargets.forEach((column) =>
      column.classList.remove(
        "border-indigo-500",
        "bg-slate-50/50",
        "dark:bg-slate-700/50"
      )
    );
  }

  dragOver(event) {
    event.preventDefault();
    const column = event.target.closest(
      "[data-appointment-kanban-target='column']"
    );
    if (!column) return;

    column.classList.add(
      "border-indigo-500",
      "bg-slate-50/50",
      "dark:bg-slate-700/50"
    );
  }

  dragEnter(event) {
    const column = event.target.closest(
      "[data-appointment-kanban-target='column']"
    );
    if (!column) return;

    column.classList.add(
      "border-indigo-500",
      "bg-slate-50/50",
      "dark:bg-slate-700/50"
    );
  }

  dragLeave(event) {
    const column = event.target.closest(
      "[data-appointment-kanban-target='column']"
    );
    if (!column) return;

    column.classList.remove(
      "border-indigo-500",
      "bg-slate-50/50",
      "dark:bg-slate-700/50"
    );
  }

  updateColumnCounters(fromColumn, toColumn) {
    const fromCounter = fromColumn.querySelector(
      "[data-appointment-kanban-target='counter']"
    );
    const toCounter = toColumn.querySelector(
      "[data-appointment-kanban-target='counter']"
    );

    const fromCount = fromColumn.querySelectorAll(
      "[data-appointment-kanban-target='card']"
    ).length;
    const toCount = toColumn.querySelectorAll(
      "[data-appointment-kanban-target='card']"
    ).length;

    fromCounter.textContent = `${fromCount} Appointments`;
    toCounter.textContent = `${toCount} Appointments`;
  }

  cancelMove() {
    const originalDropzone = this.originalColumn.querySelector(
      "[data-appointment-kanban-target='dropzone']"
    );
    originalDropzone.appendChild(this.draggedCard);
    this.emailModalTarget.classList.add("hidden");
  }

  async updateAppointmentStatus(appointmentId, status) {
    const newColumn = this.columnTargets.find(
      (column) => column.dataset.status === status
    );
    const dropzone = newColumn.querySelector(
      "[data-appointment-kanban-target='dropzone']"
    );
    const cardContainer = dropzone.querySelector(".space-y-2");

    // Move card with spacing
    cardContainer.appendChild(this.draggedCard);

    // Update counters immediately
    this.updateColumnCounters(this.originalColumn, newColumn);

    try {
      const response = await fetch(
        `/workspaces/${this.workspaceValue}/sites/${this.siteValue}/appointments/${appointmentId}`,
        {
          method: "PATCH",
          headers: {
            Accept: "text/vnd.turbo-stream.html",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            appointment: { status },
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update status");

      const html = await response.text();
      Turbo.renderStreamMessage(html);
    } catch (error) {
      const originalContainer = this.originalColumn
        .querySelector("[data-appointment-kanban-target='dropzone']")
        .querySelector(".space-y-2");
      originalContainer.appendChild(this.draggedCard);
      this.updateColumnCounters(newColumn, this.originalColumn);
      this.showToast(error.message || "Failed to update status", "error");
    }
  }

  async confirmMove() {
    const newColumn = this.columnTargets.find(
      (column) => column.dataset.status === this.pendingUpdate.status
    );

    this.emailModalTarget.classList.add("hidden");
    await this.updateAppointmentStatus(
      this.pendingUpdate.appointmentId,
      this.pendingUpdate.status
    );
  }
  showToast(message, type = "success") {
    this.element.dispatchEvent(
      new CustomEvent("show-toast", {
        detail: { message, type },
        bubbles: true,
      })
    );
  }
}
